import { cn } from './cn.js';

/**
 * A pastilha com as iniciais de uma pessoa.
 *
 * ## Iniciais, e foto só onde a casa guarda uma
 *
 * Foto é dado pessoal, com armazenamento e caminho de remoção próprios. Desde
 * 29/10/2026 o CONSULTOR tem os dois (a foto do vínculo, em Consultores, com
 * "Remover a foto"), e só ele: quem passa `photoUrl` desenha a foto, e todo o
 * resto continua nas iniciais. A pastilha diz o que a casa sabe e nunca
 * reserva um espaço cinza esperando uma imagem que não vem.
 *
 * ## Ela é decorativa, sempre
 *
 * O nome vem ESCRITO ao lado, em toda aparição: na lateral, no topo e na
 * célula da tabela. Por isso `aria-hidden` — sem ele, quem usa leitor de tela
 * ouviria "LG, Leandro Gomes" em cada linha da lista. Se algum dia ela aparecer
 * sozinha, quem a puser é que precisa dar o nome acessível ao redor.
 *
 * ## `vazio` não é a mesma coisa que ausência
 *
 * "Sem responsável" é um fato da demanda, não uma pessoa sem nome. Ele desenha
 * o traço do desenho de 05/09 — mesma caixa, sem cor de identidade — para a
 * linha não perder o alinhamento das outras. Uma célula vazia faria a coluna
 * parecer quebrada; uma pastilha com "—" diz que a pergunta foi respondida.
 */
const SIZES = {
  sm: 'size-5.5 rounded-pequeno text-[0.625rem]',
  md: 'size-8 rounded-pequeno text-xs',
  lg: 'size-14 rounded-borda text-base',
  xl: 'size-16 rounded-grande text-lg',
  retrato: 'size-28 rounded-cartao text-xl',
} as const;

export function Avatar({
  nome,
  size = 'md',
  tone = 'escura',
  photoUrl = null,
  className,
}: {
  /** `null` desenha a pastilha vazia — "sem responsável", "sem dono". */
  nome: string | null;
  /**
   * A foto da pessoa, quando a casa guarda uma — hoje, só a do consultor.
   * Ocupa a mesma caixa das iniciais, recortada (`object-cover`); a pastilha
   * vazia ignora a foto, porque "sem responsável" não é uma pessoa.
   */
  photoUrl?: string | null;
  /**
   * `sm` é a da célula de tabela; `md` é a da moldura; `lg` e `xl` são as do
   * pódium do ranking, e existem só lá.
   *
   * Elas param em 64px de propósito. O desenho de origem pedia 115px, o que faz
   * sentido para uma foto e não para duas letras — sem foto no produto, um
   * disco daquele tamanho é uma inicial gigante cercada de nada. A hierarquia
   * entre o 1º e os outros dois é carregada pela altura do degrau e pelo selo
   * de posição; o avatar só acompanha.
   *
   * `retrato` (112px, 30/10/2026) é a exceção que o argumento acima previa: a
   * ficha do consultor, onde há foto de verdade e ela é o assunto do bloco.
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'retrato';
  /**
   * `escura` é a pastilha navy sobre superfície clara; `marca` é a laranja, e
   * ela existe para o lado de DENTRO do chrome — hoje, o canto direito do
   * cabeçalho navy do portal no telefone.
   *
   * Por isso ela usa `chrome-primaria` e não `primaria`: são dois laranjas
   * diferentes, um para cada fundo. O `primaria` é a cor da AÇÃO, fechada para
   * ser lida sobre branco, e sobre o navy ele dava 3,35:1 — passava como
   * elemento gráfico e mais nada. Pior: ao lado dele, no mesmo cabeçalho, mora
   * o quadrado da marca em `chrome-primaria`, e as duas peças saíam em laranjas
   * que não batem. Ver `apps/portal/src/components/Layout.tsx`.
   */
  tone?: 'escura' | 'marca' | 'vazio';
  className?: string;
}) {
  /*
   * Nome em branco conta como AUSÊNCIA, e não como um nome sem iniciais.
   * `initialsOf('')` devolvia um espaço, e a pastilha saía vazia de verdade —
   * uma caixa cinza muda no meio de uma coluna de pastilhas com letra, que lê
   * como defeito de carregamento em vez de "sem responsável".
   */
  const empty = nome === null || nome.trim() === '' || tone === 'vazio';

  if (photoUrl && !empty) {
    return (
      <img
        aria-hidden
        alt=""
        src={photoUrl}
        className={cn(
          'shrink-0 bg-superficie-suave object-cover',
          SIZES[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      aria-hidden
      className={cn(
        'grid shrink-0 place-items-center font-bold',
        /*
         * O raio acompanha o tamanho, e é por isso que ele não é fixo.
         * `rounded-pequeno` são 6px: numa pastilha de 22px eles arredondam de
         * verdade, e num disco de 64px viram um quadrado com o canto lascado. A
         * pastilha continua sendo a mesma peça — o que muda é a escala dela, não
         * a forma. Círculo aqui abriria um segundo desenho de avatar para uma
         * tela só.
         */
        SIZES[size],
        empty
          ? 'bg-superficie-suave text-texto-tenue'
          : tone === 'marca'
            ? 'bg-chrome-primaria text-chrome'
            : 'bg-chrome-alto text-chrome-texto',
        className,
      )}
    >
      {empty ? '—' : initialsOf(nome)}
    </span>
  );
}

/**
 * Duas iniciais: a primeira palavra e a última.
 *
 * Do meio não sai nada — "Maria Juliana de Melo" é `MM` e não `MJDM`. Nome
 * brasileiro tem preposição no meio (`de`, `da`, `dos`), e uma inicial por
 * palavra transformaria a pastilha numa sigla ilegível de cinco letras.
 */
export function initialsOf(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  /* Quem chama pelo `Avatar` nunca chega aqui vazio — ver `empty`, lá em cima. */
  if (parts.length === 0) return '—';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}
